<script setup>
import { computed, onMounted, ref } from 'vue'
import AdminActionDialog from '../components/AdminActionDialog.vue'
import LifecycleStatusBadge from '../components/LifecycleStatusBadge.vue'
import {
  createOrganizationRequest,
  getDeletePreview,
  softDeleteOrganization,
  fetchAllOrganizationsRequest,
  fetchSubOrgsRequest,
  restoreOrganization,
} from '../services/organizationService.js'
import { toOrganization } from '../domain/model.js'
import { getAdminEmptyStateMessage, parseAdminError, toLifecycleIntegrityMessage, toLifecycleWarningMessage } from '../utils/adminUi.js'
import { getDeletedOrgs, filterActiveOrgs, detectRestoreConflicts, validateLifecycleHierarchy } from '../utils/lifecycleUtils.js'

const organizations = ref([])
const loading = ref(false)
const error = ref('')
const creating = ref(false)
const createError = ref('')
const createMessage = ref('')
const actionError = ref('')
const actionMessage = ref('')
const orgName = ref('')
const statusFilter = ref('all')
const rowLocks = ref({})
const mutating = ref(false)
let requestToken = 0
const pendingRestoreOrg = ref(null)
const pendingDeleteOrg = ref(null)
const pendingDeletePreview = ref(null)
const subOrgs = ref([])
const lifecycleIntegrityWarning = ref('')

const activeOrganizations = computed(() => filterActiveOrgs(organizations.value))
const deletedOrganizations = computed(() => getDeletedOrgs(organizations.value))
const listBusy = computed(() => loading.value)
const emptyStateMessage = computed(() => getAdminEmptyStateMessage('organizations', {
  statusFilter: statusFilter.value,
}))
const restoreDialogMessage = computed(() => {
  if (!pendingRestoreOrg.value) return ''
  return 'This organization will become active again.'
})
const restoreDialogDetails = computed(() => ([
  'Existing audit history will be preserved.',
]))
const deleteDialogMessage = computed(() => {
  if (!pendingDeleteOrg.value) return ''
  return 'This organization will be soft-deleted and remain reversible through restore.'
})
const deleteDialogDetails = computed(() => ([
  'Delete is reversible and intended for lifecycle safety workflows.',
  'Related sub-organizations, users, and lotteries must remain read-only while deleted.',
  ...(pendingDeletePreview.value?.summary ? [String(pendingDeletePreview.value.summary)] : []),
]))

const isRowLocked = (orgId) => Boolean(rowLocks.value[String(orgId)])
const setRowLocked = (orgId, locked) => {
  rowLocks.value = {
    ...rowLocks.value,
    [String(orgId)]: Boolean(locked),
  }
}

const toOrgLifecycleWarning = (org) => {
  return toLifecycleWarningMessage({
    org,
    hasSubOrgs: true,
    hasUsers: true,
    hasLotteries: true,
  })
}

const loadOrganizations = async () => {
  const token = ++requestToken
  loading.value = true
  error.value = ''
  lifecycleIntegrityWarning.value = ''

  try {
    const [data, subOrgData] = await Promise.all([
      fetchAllOrganizationsRequest(statusFilter.value),
      fetchSubOrgsRequest('all'),
    ])
    if (token !== requestToken) return
    organizations.value = Array.isArray(data)
      ? data.map((item) => ({
          ...item,
          ...toOrganization(item),
        }))
      : []
    subOrgs.value = Array.isArray(subOrgData) ? subOrgData : []

    const integrity = validateLifecycleHierarchy({
      organizations: organizations.value,
      subOrgs: subOrgs.value,
    })
    lifecycleIntegrityWarning.value = toLifecycleIntegrityMessage(integrity)
  } catch (err) {
    error.value = err?.response?.data?.detail ?? 'Failed to load organizations'
  } finally {
    if (token === requestToken) {
      loading.value = false
    }
  }
}

const createOrg = async () => {
  createError.value = ''
  createMessage.value = ''
  actionError.value = ''
  actionMessage.value = ''

  const name = orgName.value.trim()
  if (!name) {
    createError.value = 'Organization name is required.'
    return
  }

  creating.value = true

  try {
    const created = await createOrganizationRequest({ name })
    createMessage.value = `Organization "${created?.name ?? name}" created successfully.`
    orgName.value = ''
    await loadOrganizations()
  } catch (err) {
    createError.value = parseAdminError(err, 'Failed to create organization')
  } finally {
    creating.value = false
  }
}

const openRestoreDialog = (org) => {
  if (!org?.id || mutating.value || isRowLocked(org.id)) return
  createError.value = ''
  createMessage.value = ''
  actionError.value = ''
  actionMessage.value = ''
  pendingRestoreOrg.value = org
}

const closeRestoreDialog = () => {
  if (mutating.value) return
  pendingRestoreOrg.value = null
}

const openDeleteDialog = (org) => {
  if (!org?.id || mutating.value || isRowLocked(org.id)) return
  createError.value = ''
  createMessage.value = ''
  actionError.value = ''
  actionMessage.value = ''
  pendingDeletePreview.value = null
  pendingDeleteOrg.value = org
}

const closeDeleteDialog = () => {
  if (mutating.value) return
  pendingDeleteOrg.value = null
  pendingDeletePreview.value = null
}

const confirmRestoreOrg = async () => {
  const org = pendingRestoreOrg.value
  if (!org?.id || mutating.value || isRowLocked(org.id)) return

  const conflict = detectRestoreConflicts({
    organization: org,
    organizations: organizations.value,
    subOrgs: subOrgs.value,
  })
  if (conflict.hasConflict) {
    actionError.value = conflict.conflicts.join(' ')
    return
  }

  createError.value = ''
  createMessage.value = ''
  actionError.value = ''
  actionMessage.value = ''
  error.value = ''
  mutating.value = true
  setRowLocked(org.id, true)

  try {
    const restored = await restoreOrganization(org.id)
    actionMessage.value = `Organization restored successfully: ${restored?.name ?? org.name}.`
    await loadOrganizations()
  } catch (err) {
    actionError.value = parseAdminError(err, 'Failed to restore organization', {
      notFound: 'This organization is no longer available. Refresh the list and try again.',
      forbidden: 'You no longer have permission to restore this organization.',
    })
    await loadOrganizations()
  } finally {
    setRowLocked(org.id, false)
    mutating.value = false
    pendingRestoreOrg.value = null
  }
}

const confirmDeleteOrg = async () => {
  const org = pendingDeleteOrg.value
  if (!org?.id || mutating.value || isRowLocked(org.id)) return

  createError.value = ''
  createMessage.value = ''
  actionError.value = ''
  actionMessage.value = ''
  error.value = ''
  mutating.value = true
  setRowLocked(org.id, true)

  try {
    pendingDeletePreview.value = await getDeletePreview(org.id)
  } catch {
    pendingDeletePreview.value = null
  }

  try {
    const deleted = await softDeleteOrganization(org.id, { mode: 'soft_delete' })
    actionMessage.value = `Organization soft-deleted successfully: ${deleted?.name ?? org.name}. Restore is available.`
    await loadOrganizations()
  } catch (err) {
    actionError.value = parseAdminError(err, 'Failed to delete organization', {
      notFound: 'This organization is no longer available. Refresh the list and try again.',
      forbidden: 'You no longer have permission to delete this organization.',
      conflict: 'Delete blocked due to lifecycle conflict. Resolve child-state conflicts and retry.',
    })
    await loadOrganizations()
  } finally {
    setRowLocked(org.id, false)
    mutating.value = false
    pendingDeleteOrg.value = null
    pendingDeletePreview.value = null
  }
}

onMounted(() => {
  loadOrganizations()
})
</script>

<template>
  <div class="superadmin-panel">
    <h1>Global Organization Admin</h1>

    <section class="create-org">
      <h2>Create Organization</h2>
      <form @submit.prevent="createOrg" class="form-row">
        <input v-model="orgName" type="text" placeholder="Organization name" />
        <button type="submit" :disabled="creating">{{ creating ? 'Creating...' : 'Create Organization' }}</button>
      </form>
      <p v-if="createMessage" class="success">{{ createMessage }}</p>
      <p v-if="createError" class="error">{{ createError }}</p>
    </section>

    <section class="org-list">
      <h2>All Organizations</h2>
      <div class="toolbar">
        <label>
          Status
          <select v-model="statusFilter" :disabled="listBusy" @change="loadOrganizations">
            <option value="all">All</option>
            <option value="active">Active</option>
            <option value="deleted">Deleted</option>
          </select>
        </label>
        <button @click="loadOrganizations" :disabled="listBusy">{{ loading ? 'Loading...' : 'Refresh' }}</button>
      </div>
      <p class="muted">Active: {{ activeOrganizations.length }} | Deleted: {{ deletedOrganizations.length }}</p>
      <p v-if="error" class="error">{{ error }}</p>
      <p v-if="lifecycleIntegrityWarning" class="warning">{{ lifecycleIntegrityWarning }}</p>
      <p v-if="actionMessage" class="success action-feedback">{{ actionMessage }}</p>
      <p v-if="actionError" class="error action-feedback">{{ actionError }}</p>
      <p v-if="!loading && !organizations.length" class="muted empty-state">{{ emptyStateMessage }}</p>
      <ul v-else>
        <li v-for="org in organizations" :key="org.id">
          <strong>#{{ org.id }} - {{ org.name }}</strong>
          <LifecycleStatusBadge :entity="org" />
          <p v-if="org.is_deleted" class="deleted-note">{{ toOrgLifecycleWarning(org) }}</p>
          <div class="row-actions">
            <button
              v-if="!org.is_deleted"
              class="delete-btn"
              type="button"
              :disabled="isRowLocked(org.id) || loading || mutating"
              :data-testid="`org-delete-${org.id}`"
              @click="openDeleteDialog(org)"
            >
              {{ isRowLocked(org.id) ? 'Working...' : 'Soft-delete' }}
            </button>
          <button
            v-if="org.is_deleted"
            class="restore-btn"
            type="button"
            :disabled="isRowLocked(org.id) || loading || mutating"
            :data-testid="`org-restore-${org.id}`"
            @click="openRestoreDialog(org)"
          >
            {{ isRowLocked(org.id) ? 'Restoring...' : 'Restore' }}
          </button>
          </div>
        </li>
      </ul>
    </section>

    <AdminActionDialog
      :open="Boolean(pendingRestoreOrg)"
      title="Restore Organization"
      :message="restoreDialogMessage"
      :details="restoreDialogDetails"
      confirm-label="Restore Organization"
      loading-label="Restoring organization..."
      tone="success"
      :loading="mutating"
      @close="closeRestoreDialog"
      @confirm="confirmRestoreOrg"
    />

    <AdminActionDialog
      :open="Boolean(pendingDeleteOrg)"
      title="Soft-delete Organization"
      :message="deleteDialogMessage"
      :details="deleteDialogDetails"
      confirm-label="Soft-delete Organization"
      loading-label="Deleting organization..."
      tone="danger"
      :loading="mutating"
      @close="closeDeleteDialog"
      @confirm="confirmDeleteOrg"
    />
  </div>
</template>

<style scoped>
.superadmin-panel {
  max-width: 960px;
  margin: 0 auto;
  padding: 2rem;
  display: grid;
  gap: 1.5rem;
}

h1,
h2 {
  color: #111827;
}

.form-row {
  display: flex;
  gap: 0.5rem;
}

.toolbar {
  display: flex;
  gap: 0.75rem;
  align-items: end;
}

.toolbar label {
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
  font-size: 0.9rem;
  color: #374151;
}

input {
  min-width: 280px;
  padding: 0.5rem;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
}

button {
  border: none;
  border-radius: 6px;
  background: #2563eb;
  color: #fff;
  padding: 0.5rem 0.85rem;
}

.success {
  color: #166534;
}

.error {
  color: #b91c1c;
}

.muted {
  color: #6b7280;
  font-size: 0.9rem;
}

.deleted-note {
  color: #92400e;
  font-size: 0.85rem;
}

.restore-btn {
  margin-top: 0.35rem;
  background: #047857;
}

.delete-btn {
  margin-top: 0.35rem;
  background: #b91c1c;
}

.row-actions {
  display: flex;
  gap: 0.5rem;
}

.warning {
  color: #92400e;
  background: #fffbeb;
  border: 1px solid #fde68a;
  border-radius: 6px;
  padding: 0.45rem;
}

.action-feedback,
.empty-state {
  margin-top: 0.75rem;
}

ul {
  list-style: none;
  padding: 0;
  margin: 0.75rem 0 0;
  display: grid;
  gap: 0.35rem;
}
</style>

